import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';
import BasicSelector from '../components/ui/BasicSelector.jsx';
import { setCurrentCompany } from '../store/currentCompanySlice.js';
import { setCurrentBranch } from '../store/currentBranchSlice.js';
import { setSettings } from '../store/settingSlice.js';
import { setLang } from '../store/langSlice.js';
import { arrayToObjKeyValue } from '../utils/global.js';

const CENTRAL_SETTINGS_SCOPE = 'global';
const CENTRAL_SELECTION_SCOPE = 'header-selection';
const CENTRAL_SETTINGS_CACHE_PREFIX = 'centralSettingsCache:';
const normalizeLanguageCode = (value) => String(value || '').trim().toLowerCase();

const TenantBar = ({ companiesList, branchesList, companySelected, branchSelected }) => {
  const dispatch = useDispatch();
  const { i18n } = useTranslation('global');
  const user = useSelector((state) => state.auth).user;
  const langState = useSelector((state) => state.lang);
  const [openListCompanies, setOpenListCompanies] = useState(false);
  const [openListBranches, setOpenListBranches] = useState(false);
  const [recentUserSelection, setRecentUserSelection] = useState(null);

  const safeParse = (value) => {
    if (!value) return null;
    try {
      return JSON.parse(value);
    } catch {
      return null;
    }
  };

  const mergeUserSettings = (settings, name, value) => {
    const list = Array.isArray(settings) ? settings : [];
    const filtered = list.filter((item) => item?.name !== name);
    if (value === undefined) return filtered;
    return [...filtered, { name, value }];
  };

  const updateStoredUser = (payload) => {
    const existing = safeParse(localStorage.getItem('user')) || {};
    const next = { ...existing, ...payload };
    localStorage.setItem('user', JSON.stringify(next));
  };

  const updateCurrentUser = async (payload) => {
    if (!payload) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_Auth_API_BASE_URL}/users/update`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: () => true
        }
      );
      if (response?.status === 200 && response?.data?.data) {
        updateStoredUser(response.data.data);
      }
    } catch (error) {
    }
  };

  const loadCentralSettings = async (companyId) => {
    if (!user?.tenant_id || !companyId) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    const cacheKey = `${CENTRAL_SETTINGS_CACHE_PREFIX}${user.tenant_id}:${companyId}:${CENTRAL_SETTINGS_SCOPE}`;
    const cachedRaw = (() => {
      try {
        return JSON.parse(localStorage.getItem(cacheKey));
      } catch {
        return null;
      }
    })();
    const normalizeSettingsBlock = (value) => {
      if (!value) return {};
      if (Array.isArray(value)) return arrayToObjKeyValue(value);
      return value;
    };
    if (cachedRaw?.data) {
      dispatch(setSettings({
        invoiceSettings: normalizeSettingsBlock(cachedRaw?.data?.invoiceSettings),
        generalSettings: normalizeSettingsBlock(cachedRaw?.data?.generalSettings),
        quoteSettings: normalizeSettingsBlock(cachedRaw?.data?.quoteSettings)
      }));
    }
    const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&companyId=${companyId}&scope=${CENTRAL_SETTINGS_SCOPE}`;
    try {
      const headers = {
        Authorization: `Bearer ${token}`
      };
      if (cachedRaw?.version) {
        headers['If-None-Match'] = String(cachedRaw.version);
      }
      const response = await axios.get(url, { headers, validateStatus: () => true });
      if (response.status === 304) return;
      if (response?.data?.status === 1) {
        const responseData = response?.data?.data;
        if (!responseData && cachedRaw?.data) return;
        const nextData = responseData || {};
        const nextVersion = response?.data?.version ?? cachedRaw?.version ?? 0;
        localStorage.setItem(cacheKey, JSON.stringify({ data: nextData, version: nextVersion }));
        dispatch(setSettings({
          invoiceSettings: normalizeSettingsBlock(nextData?.invoiceSettings),
          generalSettings: normalizeSettingsBlock(nextData?.generalSettings),
          quoteSettings: normalizeSettingsBlock(nextData?.quoteSettings)
        }));
      }
    } catch (error) {
    }
  };
  const getTenantData = () => safeParse(localStorage.getItem('tenant'));
  const getCompanyById = (tenantData, companyId) =>
    tenantData?.companies?.find((company) => company?._id === companyId);
  const getBranchById = (tenantData, companyId, branchId) => {
    const company = getCompanyById(tenantData, companyId);
    return company?.branches?.find((branch) => branch?._id === branchId);
  };
  const getNameByLang = (nameList, preferredLang = 'en') => {
    if (!Array.isArray(nameList)) return '';
    const currentLang = String(preferredLang || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
    const fallbackLang = currentLang === 'ar' ? 'en' : 'ar';
    return (
      nameList.find((n) => n?.lang === currentLang)?.text ||
      nameList.find((n) => n?.lang === fallbackLang)?.text ||
      nameList?.[0]?.text ||
      ''
    );
  };
  const getLangLabel = (langCode) => {
    const normalized = normalizeLanguageCode(langCode);
    if (!normalized) return '';
    return normalized === 'ar' ? 'عربي' : normalized === 'en' ? 'English' : normalized;
  };
  const loadCentralSelection = async () => {
    if (!user?.tenant_id) return;
    
    // Check for recent user selections (within last 2 seconds)
    if (recentUserSelection && (Date.now() - recentUserSelection.timestamp < 2000)) {
      return; // Skip loading central selection if user just made a selection
    }
    
    const token = localStorage.getItem('token');
    if (!token) return;
    const url = `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central?tenantId=${user.tenant_id}&scope=${CENTRAL_SELECTION_SCOPE}`;
    try {
    const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` }, validateStatus: () => true });
      if (response?.data?.status !== 1) return;
    const selection = response?.data?.data;
      if (!selection) return;
    const responseCompany = response?.data?.currentCompany;
    const responseBranch = response?.data?.currentBranch;
      const tenantData = getTenantData();
      const selectedCompanyId = selection?.selectedCompanyId || selection?.companyId;
      const selectedBranchId = selection?.selectedBranchId || selection?.branchId;
      const selectedLang = selection?.selectedLang || selection?.lang || selection?.language;
      const effectiveLang = selectedLang || langState?.value;
      if (selectedCompanyId) {
        const company = getCompanyById(tenantData, selectedCompanyId);
      const companyName = responseCompany?.name ? getNameByLang(responseCompany.name, effectiveLang) : getNameByLang(company?.name, effectiveLang);
      if (company || companyName) {
          dispatch(setCurrentCompany({
          name: companyName,
            value: selectedCompanyId
          }));
        }
      }
      const effectiveCompanyId = selectedCompanyId || companySelected?.value;
      if (effectiveCompanyId && selectedBranchId) {
        const branch = getBranchById(tenantData, effectiveCompanyId, selectedBranchId);
      const branchName = responseBranch?.name ? getNameByLang(responseBranch.name, effectiveLang) : getNameByLang(branch?.name, effectiveLang);
      if (branch || branchName) {
          dispatch(setCurrentBranch({
          name: branchName,
            value: selectedBranchId
          }));
        }
      }
      if (selectedLang) {
        const normalizedSelectedLang = normalizeLanguageCode(selectedLang);
        dispatch(setLang({ label: getLangLabel(normalizedSelectedLang), value: normalizedSelectedLang }));
        i18n.changeLanguage(normalizedSelectedLang);
      }
    } catch (error) {
    }
  };
  const saveCentralSelection = async (selectedCompanyId, selectedBranchId, selectedLang) => {
    if (!user?.tenant_id) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    try {
      await axios.put(
        `${process.env.REACT_APP_Auth_API_BASE_URL}/settings/central`,
        {
          tenantId: user.tenant_id,
          scope: CENTRAL_SELECTION_SCOPE,
          data: {
            selectedCompanyId,
            selectedBranchId,
            selectedLang: selectedLang || langState?.value
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: () => true
        }
      );
    } catch (error) {
    }
  };

  const onSelectedCompany = async (company) => {
    dispatch(setCurrentCompany({ name: company.label, value: company.value }));
    setRecentUserSelection({ type: 'company', value: company.value, timestamp: Date.now() });
    const nextUserSettings = mergeUserSettings(user?.userSettings, 'selectedCompany', company.value);
    await updateCurrentUser({
      company_id: company.value,
      userSettings: nextUserSettings
    });
    await saveCentralSelection(company.value, branchSelected?.value, langState?.value);
    loadCentralSettings(company.value);
  };

  const onSelectedBranch = async (branch) => {
    dispatch(setCurrentBranch({ name: branch.label, value: branch.value }));
    setRecentUserSelection({ type: 'branch', value: branch.value, timestamp: Date.now() });
    const nextUserSettings = mergeUserSettings(user?.userSettings, 'selectedBranch', branch.value);
    await updateCurrentUser({
      branch_id: branch.value,
      company_id: companySelected?.value,
      userSettings: nextUserSettings
    });
    await saveCentralSelection(companySelected?.value, branch.value, langState?.value);
    loadCentralSettings(companySelected?.value);
  };
  useEffect(() => {
    loadCentralSelection();
  }, [user?.tenant_id, user?.company_id, companySelected?.value, recentUserSelection]);

  return (
    <div className="flex flex-wrap md:flex-nowrap justify-center items-center text-xs ms-4 gap-0 w-max">
      <BasicSelector
        border={false}
        label={companySelected?.name}
        defaultValue={companySelected?.name}
        listItem={companiesList}
        openList={openListCompanies}
        setOpenList={setOpenListCompanies}
        onSelected={onSelectedCompany}
      >
        <img className="w-5" src="/assets/icons/company-svgrepo-com 1.png" alt="Company" />
      </BasicSelector>

      <BasicSelector
        border={false}
        label={branchSelected?.name}
        defaultValue={branchSelected?.name}
        listItem={branchesList}
        openList={openListBranches}
        setOpenList={setOpenListBranches}
        onSelected={onSelectedBranch}
      >
        <img className="w-5" src="/assets/icons/location.png" alt="Branch" />
      </BasicSelector>
    </div>
  );
};

export default TenantBar;
